import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface FetchConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

interface UseAxiosFetchReturn {
  isLoading: boolean;
  fetchData: (configParams?: FetchConfig) => Promise<AxiosResponse | AxiosError>;
}

const useAxiosFetch = (): UseAxiosFetchReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { storeNo } = useParams<{ storeNo: string }>();

  const useremail = localStorage.getItem('useremail'); // 이메일은 쿠키로 안 옮겼다면 그대로 사용

  const refetchJwt = async (): Promise<boolean> => {
    if (!useremail) {
      // 현재 경로를 URL 파라미터로 전달하여 로그인 후 리다이렉트
      // 단, consent 페이지나 로그인 페이지가 아닌 경우에만 redirect 파라미터 설정
      const currentPath = window.location.pathname + window.location.search;
      const isConsentPage = currentPath.includes('/consent');
      const isLoginPage = currentPath.includes('/login/');
      const redirectParam = (isConsentPage || isLoginPage) ? '' : encodeURIComponent(currentPath);
      const loginUrl = redirectParam 
        ? `/login/${storeNo}?redirect=${redirectParam}`
        : `/login/${storeNo}`;
      navigate(loginUrl, {
        replace: true,
        state: { showLoginRequiredAlert: true },
      });
      return false;
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_API_SERVER}/refresh`,
        {}, // 빈 객체
        { withCredentials: true } // 세 번째 매개변수로 설정
      );
      return true;
    } catch (e) {
      const error = e as AxiosError;
      const status = error?.response?.status || (error?.request as any)?.status;
      if (status === 401) {
        // 현재 경로를 URL 파라미터로 전달하여 로그인 후 리다이렉트
        // 단, consent 페이지나 로그인 페이지가 아닌 경우에만 redirect 파라미터 설정
        const currentPath = window.location.pathname + window.location.search;
        const isConsentPage = currentPath.includes('/consent');
        const isLoginPage = currentPath.includes('/login/');
        const redirectParam = (isConsentPage || isLoginPage) ? '' : encodeURIComponent(currentPath);
        const loginUrl = redirectParam 
          ? `/login/${storeNo}?redirect=${redirectParam}`
          : `/login/${storeNo}`;
        // 리프레시 토큰 만료 등 401 발생 시 로그인 페이지로 이동
        navigate(loginUrl, {
          replace: true,
          state: { showLoginRequiredAlert: true },
        });
      } else {
        console.log(error);
      }
      return false;
    }
  };

  const fetchData = async (configParams?: FetchConfig): Promise<AxiosResponse | AxiosError> => {
    // URL이 이미 전체 URL인 경우 baseURL을 사용하지 않음
    // Home.js에서는 REACT_APP_API_SERVER_PUBLIC을 사용하여 전체 URL을 만들고 있음
    const isFullUrl = configParams?.url?.startsWith('http://') || configParams?.url?.startsWith('https://');
    
    const axiosInstance = axios.create({
      baseURL: isFullUrl ? '' : process.env.REACT_APP_API_SERVER, // 전체 URL이면 baseURL 비우기
      withCredentials: true, // 다른 도메인 (Cross-Origin) 요청에서 쿠키 사용
    });

    setIsLoading(true);
    try {
      const response = await axiosInstance(configParams as AxiosRequestConfig);
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      const status = axiosError?.response?.status || (axiosError?.request as any)?.status;
      if (status === 401) {
        // 한 번만 재시도하도록 가드
        if (configParams && configParams._retry) {
          // 현재 경로를 URL 파라미터로 전달하여 로그인 후 리다이렉트
          // 단, consent 페이지나 로그인 페이지가 아닌 경우에만 redirect 파라미터 설정
          const currentPath = window.location.pathname + window.location.search;
          const isConsentPage = currentPath.includes('/consent');
          const isLoginPage = currentPath.includes('/login/');
          const redirectParam = (isConsentPage || isLoginPage) ? '' : encodeURIComponent(currentPath);
          const loginUrl = redirectParam 
            ? `/login/${storeNo}?redirect=${redirectParam}`
            : `/login/${storeNo}`;
          navigate(loginUrl, {
            replace: true,
            state: { showLoginRequiredAlert: true },
          });
          return axiosError.response as AxiosResponse;
        }

        const refreshed = await refetchJwt();
        if (!refreshed) {
          return axiosError.response as AxiosResponse;
        }

        const retryConfig = { ...(configParams || {}), _retry: true };
        return await axiosInstance(retryConfig as AxiosRequestConfig);
      } else {
        console.log(axiosError);
        // 에러가 발생해도 error.response가 있으면 반환, 없으면 error 자체 반환
        return axiosError.response || axiosError;
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, fetchData };
};

export default useAxiosFetch;
